// frontend/packages/business-console/src/components/layout/MobileBottomNav.jsx
//
// Persistent bottom tab bar shown only on mobile (see
// docs/design/mobile-console-ux.md §4). Puts the four high-frequency
// destinations in the thumb zone so the common on-the-go tasks are one tap
// away instead of two-taps-behind-the-hamburger. The hamburger drawer still
// exists for the long tail (CRM, Quotes, Invoices, Media, etc.).
//
// Tabs mirror pageConfig route keys (Home / Pulse / Inbox / Create); Inbox
// carries the shared unread badge. Active tab is resolved by longest path
// match so detail routes (e.g. /inbox/:id) keep Inbox highlighted. On a
// non-tab page (CRM, Profile, …) nothing is highlighted, which is correct.
//
// In school district mode (districtMode.js) Pulse and Create point at the
// hidden sales stack, so the bar becomes Home / Inbox / Content, plus Alerts
// when the tenant has emergency alerts on.

import React, { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import { BusinessContext } from '../../contexts/BusinessContext';
import { useInboxUnread } from '../../contexts/InboxUnreadContext';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import { isDistrictMode } from './districtMode';

// Revision 2 tab set (docs/design/mobile-console-ux.md §4.1):
// Home (feed) · Pulse (glance) · Inbox (reactive) · Create (proactive).
const TABS = [
  { key: 'home', label: 'Home', path: '', Icon: HomeOutlinedIcon },
  {
    key: 'pulse',
    label: 'Pulse',
    path: 'pulse',
    Icon: MonitorHeartOutlinedIcon,
  },
  { key: 'inbox', label: 'Inbox', path: 'inbox', Icon: InboxOutlinedIcon },
  {
    key: 'create',
    label: 'Create',
    path: 'create',
    Icon: AddCircleOutlineIcon,
  },
];

const DISTRICT_TABS = [
  { key: 'home', label: 'Home', path: '', Icon: HomeOutlinedIcon },
  { key: 'inbox', label: 'Inbox', path: 'inbox', Icon: InboxOutlinedIcon },
  {
    key: 'content',
    label: 'Content',
    path: 'content',
    Icon: DescriptionOutlinedIcon,
  },
  {
    key: 'site-alerts',
    label: 'Alerts',
    path: 'site-alerts',
    Icon: ReportProblemOutlinedIcon,
    feature: 'emergency_alerts',
  },
];

export default function MobileBottomNav() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { business } = useContext(BusinessContext);
  const unread = useInboxUnread();
  const { features } = useTenantFeatures();
  const tabs = useMemo(
    () =>
      isDistrictMode(features)
        ? DISTRICT_TABS.filter(
            (t) => !t.feature || features[t.feature] === true
          )
        : TABS,
    [features]
  );

  // Same basePath convention as Sidebar.jsx: /business/<slug>.
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  const hrefFor = (tab) => (tab.path ? `${basePath}/${tab.path}` : basePath);

  // Longest-prefix match so /inbox/:id keeps Inbox lit. Home (path "") only
  // matches the bare basePath, never as a prefix of everything.
  const selected = useMemo(() => {
    const current = location.pathname;
    let best = null;
    let bestLen = -1;
    for (const tab of tabs) {
      const href = hrefFor(tab);
      const matches = tab.path
        ? current === href || current.startsWith(`${href}/`)
        : current === basePath || current === `${basePath}/`;
      if (matches && href.length > bestLen) {
        best = tab.key;
        bestLen = href.length;
      }
    }
    return best;
  }, [location.pathname, basePath, tabs]);

  const handleChange = (_event, key) => {
    const tab = tabs.find((t) => t.key === key);
    if (tab) navigate(hrefFor(tab) || '/');
  };

  return (
    <Paper
      elevation={0}
      square
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: theme.zIndex.appBar,
        borderTop: 1,
        borderColor: 'divider',
        // Lift above the home-indicator on notched phones.
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      <BottomNavigation
        value={selected}
        onChange={handleChange}
        showLabels
        sx={{
          height: theme.custom.mobile.bottomNavHeight,
          bgcolor: 'background.paper',
        }}
      >
        {tabs.map(({ key, label, Icon }) => {
          const icon =
            key === 'inbox' && unread > 0 ? (
              <Badge color="error" badgeContent={unread} max={99}>
                <Icon />
              </Badge>
            ) : (
              <Icon />
            );
          return (
            <BottomNavigationAction
              key={key}
              value={key}
              label={label}
              icon={icon}
              sx={{ minWidth: 0, py: 1 }}
            />
          );
        })}
      </BottomNavigation>
    </Paper>
  );
}
